import { createSlice } from "@reduxjs/toolkit";

import { replaceById } from "./listHelpers";
import { StatusType } from "../../assets/type.ts";

const isCompleted = item => item?.status === StatusType.COMPLETED;
const hasAccount = item => Array.isArray(item?.account) && item.account.length > 0;
const belongsTo = (item, accountId) =>
  Array.isArray(item?.account) && item.account.some(acc => acc?.id === accountId);

// The fetched items, plus the loaded ones that are not among them and that `keep` accepts
const mergeFetched = (state, items, keep) => {
  const incoming = items || [];
  const incomingIds = new Set(incoming.map(item => item.id));
  return [...state.filter(item => !incomingIds.has(item.id) && keep(item)), ...incoming];
};

// The slice holds the open items of the central list and of the personal lists loaded so far,
// and, while "Vergangene" is on, the completed items of the list on screen. Each fetch replaces
// only the part it covers, so the minute's refresh of the open lists keeps the completed ones.
const actionListSlice = createSlice({
  name: "actionList",
  initialState: [],
  reducers: {
    // The central list endpoint returns the open items no account has taken
    actionListAdd(state, action) {
      return mergeFetched(state, action.payload, item => hasAccount(item) || isCompleted(item));
    },
    // One account's open items. accountId is passed explicitly so stale items go even when the
    // account now has none.
    actionListAccountAdd(state, action) {
      const { accountId, items } = action.payload;
      return mergeFetched(state, items, item => isCompleted(item) || !belongsTo(item, accountId));
    },
    // The completed items of one list: accountId null for the central list (no account),
    // otherwise that account's personal list
    actionListCompletedReplace(state, action) {
      const { accountId, items } = action.payload;
      const inList = item => (accountId ? belongsTo(item, accountId) : !hasAccount(item));
      return mergeFetched(state, items, item => !(isCompleted(item) && inList(item)));
    },
    actionListRemoveCompleted(state) {
      return state.filter(item => item.status !== StatusType.COMPLETED);
    },
    updateActionListItemSlice(state, action) {
      const item = action.payload;

      const itemIndex = state.findIndex(i => i.id === item.id);
      if (itemIndex !== -1) {
        state[itemIndex] = item;
      }
    },
    // The server's copies of items it attached to, moved between or detached from admins
    updateActionListItemsSlice(state, action) {
      replaceById(state, action.payload);
    },
    attachActionListItemToAdminItemSlice(state, action) {
      const { actionListId, account } = action.payload;
      const item = state.find(i => i.id === actionListId);

      if (!item) {
        return;
      }
      if (Array.isArray(item.account)) {
        item.account.push(account);
      } else {
        item.account = [account];
      }
    },
    moveActionListItemBetweenAdminItemSlice(state, action) {
      const id = action.payload.actionListId;
      const account = action.payload.account;
      const itemIndex = state.findIndex(item => item.id === id);
      if (itemIndex !== -1) {
        state[itemIndex].account = account;
      }
    },
    detachActionListItemFromAdminItemSlice(state, action) {
      const id = action.payload;
      const itemIndex = state.findIndex(item => item.id === id);
      if (itemIndex !== -1) {
        state[itemIndex].account = [];
      }
    },
    actionListItemStatusUpdateItemSlice(state, action) {
      const actionListId = action.payload.actionListItemId;
      const value = action.payload.value;
      const actionListItemIndex = state.findIndex(item => item.id === actionListId);

      if (actionListItemIndex !== -1) {
        state[actionListItemIndex].status = value;
      }
    },
  },
});

export const {
  actionListAdd,
  actionListAccountAdd,
  actionListCompletedReplace,
  actionListRemoveCompleted,
  updateActionListItemSlice,
  updateActionListItemsSlice,
  attachActionListItemToAdminItemSlice,
  detachActionListItemFromAdminItemSlice,
  moveActionListItemBetweenAdminItemSlice,
  actionListItemStatusUpdateItemSlice,
} = actionListSlice.actions;
export default actionListSlice.reducer;
